// The agent permissions grid (docs/superpowers/specs/2026-10-15-agent-permissions-design.md, "The
// Admin page UI"): each area Off / Read-only / Full, each send type Off / Ask first / Allowed, and
// Auto mode.
//
// - The clinic defaults: `value` sets every key, and Auto is a switch.
// - A person's override (`allowDefault`): every row also offers "Default", which takes the key out
//   of the value (a group left empty goes with it), and Auto is Default / On / Off. With `defaults`
//   (the clinic's), each row says what its default is.
// - An area's `note` (charts: agents only read charts today) shows under its name.
// - A value the page doesn't know (a newer server's) shows with no choice pressed; so does an Auto
//   it doesn't know, as choices rather than a switch that would read as on.
//
// onChange always gets a new object; `value` is never changed in place, and what the grid doesn't
// show (a newer server's keys) rides along unchanged.
import React, { useId } from "react";
import { Box, Button, ButtonGroup, Flex, Stack, Switch, Text } from "@chakra-ui/react";
import { AREAS, AREA_VALUES, AUTO_VALUES, SENDS, SEND_VALUES, isObject, valueLabel } from "./policyWords";

// The "Default" choice. Never a value a key can hold (those are strings and booleans).
const DEFAULT = { value: "__default__", label: "Default" };

// The ids that describe a row: what its clinic default is, then its note.
const descriptionIds = (id, hint, note) =>
  [hint && `${id}-hint`, note && `${id}-note`].filter(Boolean).join(" ") || undefined;

// One row: its name, what its clinic default is (for an override), its note, and its choices or
// its switch.
function Row({ id, label, hint, note, children }) {
  return (
    <Flex align="center" justify="space-between" wrap="wrap" gap={2} py={1.5}>
      <Box minW="160px">
        <Text id={`${id}-label`} fontSize="sm">
          {label}
        </Text>
        {hint && (
          <Text id={`${id}-hint`} fontSize="xs" color="gray.500">
            {hint}
          </Text>
        )}
        {note && (
          <Text id={`${id}-note`} fontSize="xs" color="gray.500">
            {note}
          </Text>
        )}
      </Box>
      {children}
    </Flex>
  );
}

function Choices({ id, options, current, onPick, describedBy, isDisabled }) {
  return (
    <ButtonGroup
      size="xs"
      isAttached
      variant="outline"
      isDisabled={isDisabled}
      aria-labelledby={`${id}-label`}
      aria-describedby={describedBy}
    >
      {options.map((option) => {
        const on = option.value === current;
        return (
          <Button
            key={String(option.value)}
            aria-pressed={on}
            colorScheme={on ? "blue" : "gray"}
            variant={on ? "solid" : "outline"}
            onClick={() => onPick(option.value)}
          >
            {option.label}
          </Button>
        );
      })}
    </ButtonGroup>
  );
}

function Section({ title, help, children }) {
  return (
    <Box>
      <Text fontWeight="semibold" fontSize="sm">
        {title}
      </Text>
      {help && (
        <Text fontSize="xs" color="gray.500" mb={1}>
          {help}
        </Text>
      )}
      {children}
    </Box>
  );
}

export default function PolicyGrid({ value, onChange, allowDefault = false, defaults = null, isDisabled = false }) {
  const base = useId();
  const current = isObject(value) ? value : {};
  const hint = (key, clinicValue) =>
    allowDefault && isObject(defaults) && clinicValue !== undefined ? `Clinic default: ${valueLabel(key, clinicValue)}` : null;
  const withDefault = (options) => (allowDefault ? [DEFAULT, ...options] : options);
  // What a row shows as chosen: its value, or Default when an override doesn't set it.
  const shown = (setValue) => (allowDefault && setValue === undefined ? DEFAULT.value : setValue);

  const pick = (group, key) => (choice) => {
    const next = { ...current };
    const members = isObject(current[group]) ? { ...current[group] } : {};
    if (choice === DEFAULT.value) delete members[key];
    else members[key] = choice;
    if (Object.keys(members).length) next[group] = members;
    else delete next[group];
    onChange?.(next);
  };
  const pickAuto = (choice) => {
    const next = { ...current };
    if (choice === DEFAULT.value) delete next.auto;
    else next.auto = choice;
    onChange?.(next);
  };

  const rows = (group, list, options) =>
    list.map(({ key, label, note }) => {
      const id = `${base}-${group}-${key}`;
      const rowHint = hint(`${group}.${key}`, defaults?.[group]?.[key]);
      return (
        <Row key={key} id={id} label={label} hint={rowHint} note={note}>
          <Choices
            id={id}
            options={withDefault(options)}
            current={shown(isObject(current[group]) ? current[group][key] : undefined)}
            onPick={pick(group, key)}
            describedBy={descriptionIds(id, rowHint, note)}
            isDisabled={isDisabled}
          />
        </Row>
      );
    });

  const autoId = `${base}-auto`;
  const autoHint = hint("auto", defaults?.auto);
  // An Auto the page doesn't know (neither true, false nor unset) is shown as choices, none pressed.
  const autoKnown = current.auto === undefined || typeof current.auto === "boolean";
  return (
    <Stack spacing={4}>
      <Section title="Areas" help="Read-only lets agents look, but not change anything there.">
        {rows("areas", AREAS, AREA_VALUES)}
      </Section>
      <Section title="Sending" help="Ask first holds every send for approval, even in Auto mode.">
        {rows("sends", SENDS, SEND_VALUES)}
      </Section>
      <Section title="Mode">
        <Row id={autoId} label="Auto mode" hint={autoHint}>
          {allowDefault || !autoKnown ? (
            <Choices
              id={autoId}
              options={withDefault(AUTO_VALUES)}
              current={shown(current.auto)}
              onPick={pickAuto}
              describedBy={descriptionIds(autoId, autoHint)}
              isDisabled={isDisabled}
            />
          ) : (
            <Switch
              aria-labelledby={`${autoId}-label`}
              aria-describedby={`${autoId}-help`}
              isChecked={current.auto !== false}
              isDisabled={isDisabled}
              onChange={(event) => pickAuto(event.target.checked)}
            />
          )}
        </Row>
        <Text id={`${autoId}-help`} fontSize="xs" color="gray.500">
          Off: every agent runs in Ask mode, asking before every change or send.
        </Text>
      </Section>
    </Stack>
  );
}
